import React from "react";
import { Card, CardContent } from "../components/ui/card";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { CONTACT_INFO, GOOGLE_MAPS_URL, ACCESSIBILITY_OVERLAYS_ENABLED, BOOKINGS_APP_URL } from "../config/constants";
import { useLanguage } from "../contexts/LanguageProvider";
import anoViannosMap from "../assets/ano viannos map.jpeg";
import anoViannosBackground from "../assets/ano-viannos-background.jpg";

export default function Contact() {
  const { t } = useLanguage();

  const contactInfo = [
    {
      icon: Phone,
      label: t('contactPhone'),
      value: CONTACT_INFO.PHONE,
      action: `tel:${CONTACT_INFO.PHONE.replace(/\s/g, '')}`
    },
    {
      icon: Mail,
      label: t('contactEmail'),
      value: CONTACT_INFO.EMAIL,
      action: `mailto:${CONTACT_INFO.EMAIL}`
    },
    {
      icon: MapPin,
      label: t('contactAddress'),
      value: CONTACT_INFO.ADDRESS,
      action: GOOGLE_MAPS_URL
    },
    {
      icon: Clock,
      label: t('contactCheckInOut'),
      value: t('contactCheckInOutValue', { checkIn: CONTACT_INFO.CHECK_IN_HOURS }),
      action: null
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center">
        <div className="absolute inset-0 z-0">
          <img
            src={anoViannosBackground}
            alt="Ano Viannos village"
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${ACCESSIBILITY_OVERLAYS_ENABLED ? 'bg-gradient-to-r from-gray-900/70 to-gray-800/50' : 'bg-gradient-to-r from-gray-900/35 to-gray-800/25'}`}></div>
        </div>

        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight px-6 py-2 rounded-lg inline-block bg-black/60">
            {t('contactHeroTitle')}
          </h1>
          <p className="text-xl md:text-2xl text-white max-w-2xl mx-auto leading-relaxed px-6 py-3 rounded-lg font-semibold bg-black/50">
            {t('contactHeroSubtitle')}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        {/* Contact Information */}
        <div className="max-w-4xl mx-auto">
          <div className="mb-12 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('contactGetInTouch')}</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('contactDescription', { guesthouse: CONTACT_INFO.GUESTHOUSE_NAME })}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {contactInfo.map((info, index) => (
              <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">{info.label}</h3>
                      {info.action ? (
                        <a
                          href={info.action}
                          className="text-blue-600 hover:text-blue-800 transition-colors duration-300"
                          target={info.action.startsWith('http') ? '_blank' : '_self'}
                          rel={info.action.startsWith('http') ? 'noopener noreferrer' : ''}
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-gray-600">{info.value}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Booking Information */}
          <Card className="border-0 shadow-lg bg-blue-50">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('bookingInquiryTitle')}
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {t('bookingInquiryMessage')}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href={`${BOOKINGS_APP_URL}/stay`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  {t('bookOnline')}
                </a>
                <a
                  href={`tel:${CONTACT_INFO.PHONE.replace(/\s/g, '')}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-700 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                >
                  <Phone className="w-5 h-5" />
                  {CONTACT_INFO.PHONE}
                </a>
                <a
                  href={`mailto:${CONTACT_INFO.EMAIL}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-700 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                >
                  <Mail className="w-5 h-5" />
                  {CONTACT_INFO.EMAIL}
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map Section */}
        <section className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('contactMapTitle')}</h2>
            <p className="text-xl text-gray-600">
              {t('contactMapSubtitle')}
            </p>
          </div>

          <Card className="border-0 shadow-lg overflow-hidden relative">
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <img
                src={anoViannosMap}
                alt="Location on Map"
                className="w-full h-96 object-cover"
              />
            </a>
            <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
              <a
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300"
              >
                {t('contactMapButton')}
              </a>
            </div>
          </Card>
        </section>
      </div>

    </div>
  );
}
